import { bootup } from "@app/bootup";
import {
  fetchNetworkDataForPrintReport,
  findServiceById,
  getDatabaseLabel,
  selectAppsByOrgAsList,
  selectDatabasesByOrgAsList,
  selectEndpointsAsList,
  selectLogDrainsAsList,
  selectMetricDrainsAsList,
  selectServices,
  selectStacksByOrgAsList,
  selectVpcPeersAsList,
  selectVpnTunnelsAsList,
} from "@app/deploy";
import {
  MAX_SECURITY_CONTROL_SCOPE,
  computeFrameworkMetricsFromAggregates,
  fetchCustomerControls,
  fetchGlobalControls,
  selectSecurityControlAggregates,
  selectSecurityControlDefaultScope,
  selectSecurityControlEnvironments,
} from "@app/deploy/security-controls";
import type { SecurityControlAggregate } from "@app/deploy/security-controls";
import {
  selectOrganizationById,
  selectOrganizationSelectedId,
} from "@app/organizations";
import { useLoader, useSelector } from "@app/react";
import { useQuery } from "@app/react";
import { schema } from "@app/schema";
import { titleCase } from "@app/string-utils";
import { AptibleLogo } from "@app/ui/shared/aptible-logo";
import { Box } from "@app/ui/shared/box";
import { Button } from "@app/ui/shared/button";
import { LoadingSpinner } from "@app/ui/shared/loading";
import { sortSecurityControlAggregates } from "@app/ui/shared/security-controls";
import { tokens } from "@app/ui/shared/tokens";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  REPORT_BRAND_PURPLE,
  REPORT_STATUS_GREEN,
  REPORT_STATUS_RED,
  buildPrintReportComplianceOverviewRows,
  buildPrintReportFilterLines,
  buildStackCountDisplay,
  formatPrintReportCoverGeneratedAt,
  formatPrintReportFooterDate,
  getIsolatedVpcsDescriptionText,
  getIsolatedVpcsRegionStacks,
  hasPrintReportControlFilters,
  partitionScopedStacks,
  pluralizeResourceType,
  regionsForStacks,
  reportStatusContainerClass,
  statusLabels,
} from "./print-controls-helpers";
import { PrintStyles } from "./print-controls-styles";

const ReportStatusText = ({ status }: { status: string }) => {
  const label = statusLabels[status] || "Unknown";
  const containerClass =
    reportStatusContainerClass[status] ??
    "report-status-badge bg-gray-100 border border-gray-300 text-gray-600";
  return (
    <span
      className={`inline-block rounded-lg px-2 py-0.5 text-sm font-medium ${containerClass}`}
    >
      {label}
    </span>
  );
};

const ControlPrintItem = ({
  control,
}: { control: SecurityControlAggregate }) => {
  const { globalControl, cumulativeMetrics } = control;
  const frameworks =
    globalControl.frameworks.map((f) => f.displayName).join(", ") ||
    "All Frameworks";
  const docsUrl = globalControl.callToAction;

  return (
    <div className="control-item-wrap mb-4">
      <div className="control-item border border-gray-300 rounded-lg p-4 bg-white">
        <div className="control-item-header mb-2">
          <div className="flex justify-between items-baseline gap-4">
            <h4 className={tokens.type.h4}>{globalControl.name}</h4>
            <ReportStatusText status={cumulativeMetrics.status} />
          </div>
          <div className={`${tokens.type["small lighter"]} mt-1`}>
            <span>{globalControl.area}</span>
            <span className="mx-2">•</span>
            <span>{frameworks}</span>
          </div>
        </div>

        <div className="control-item-body mt-2 text-sm">
          <p className={tokens.type["small normal darker"]}>
            {globalControl.description}
          </p>

          {cumulativeMetrics.totalInScope > 0 || docsUrl ? (
            <div
              className={`mt-2 flex items-center justify-between gap-3 text-xs ${tokens.type["small lighter"]}`}
            >
              <div className="min-w-0">
                {cumulativeMetrics.totalInScope > 0 ? (
                  <>
                    <span>
                      {cumulativeMetrics.totalInScope}{" "}
                      {pluralizeResourceType(
                        cumulativeMetrics.resourceType,
                        cumulativeMetrics.totalInScope,
                      )}{" "}
                      in-scope
                    </span>
                    <span className="mx-2">•</span>
                    <span>Satisfied: {cumulativeMetrics.totalCompliant}</span>
                    {cumulativeMetrics.totalInScope -
                      cumulativeMetrics.totalCompliant -
                      cumulativeMetrics.totalIgnored >
                      0 && (
                      <>
                        <span className="mx-2">•</span>
                        <span>
                          Not Satisfied:{" "}
                          {cumulativeMetrics.totalInScope -
                            cumulativeMetrics.totalCompliant -
                            cumulativeMetrics.totalIgnored}
                        </span>
                      </>
                    )}
                    {cumulativeMetrics.totalIgnored > 0 && (
                      <>
                        <span className="mx-2">•</span>
                        <span>Ignored: {cumulativeMetrics.totalIgnored}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span>No resources in scope</span>
                )}
              </div>
              {docsUrl ? (
                <a
                  href={docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 text-gray-900 underline"
                >
                  View Docs
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface PrintControlsReportSurfaceProps {
  scopeIds: string[];
  categoryParams: string[];
  frameworkParams: string[];
  onReady?: () => void;
  onLoadingStateChange?: (state: {
    isLoading: boolean;
    isFullyLoaded: boolean;
  }) => void;
}

export const PrintControlsReportSurface = ({
  scopeIds,
  categoryParams,
  frameworkParams,
  onReady,
  onLoadingStateChange,
}: PrintControlsReportSurfaceProps) => {
  const orgId = useSelector(selectOrganizationSelectedId);
  const organization = useSelector((s) =>
    selectOrganizationById(s, { id: orgId }),
  );

  const envSets = useSelector((s) =>
    selectSecurityControlEnvironments(s, { orgId }),
  );
  const defaultScope = useSelector((s) =>
    selectSecurityControlDefaultScope(s, { orgId }),
  );

  const allEnvironments = envSets.all;

  const scopedEnvironmentIds = useMemo(() => {
    const source =
      scopeIds.length > 0
        ? scopeIds.filter((id) => allEnvironments.some((e) => e.id === id))
        : defaultScope.length > 0
          ? defaultScope
          : [...new Set(allEnvironments.map((e) => e.id))];
    return [...source].sort().slice(0, MAX_SECURITY_CONTROL_SCOPE);
  }, [scopeIds, allEnvironments, defaultScope]);

  const scopeEnvironments = useMemo(
    () => allEnvironments.filter((e) => scopedEnvironmentIds.includes(e.id)),
    [allEnvironments, scopedEnvironmentIds],
  );

  const scopeEnvIds = useMemo(
    () => new Set(scopeEnvironments.map((e) => e.id)),
    [scopeEnvironments],
  );
  const scopeStackIds = useMemo(
    () => new Set(scopeEnvironments.map((e) => e.stackId)),
    [scopeEnvironments],
  );
  const scopeStackIdsArray = useMemo(
    () => [...scopeStackIds].sort(),
    [scopeStackIds],
  );

  const bootupComplete = useSelector(
    (s) => schema.loaders.selectById(s, { id: `${bootup}` }).isSuccess === true,
  );

  useQuery(fetchGlobalControls());
  useQuery(
    fetchCustomerControls({
      orgId,
      accountIds: scopedEnvironmentIds,
    }),
  );
  useQuery(fetchNetworkDataForPrintReport({ stackIds: scopeStackIdsArray }));

  const globalControlsLoader = useLoader(fetchGlobalControls);
  const customerControlsLoader = useLoader(fetchCustomerControls);
  const networkDataLoader = useLoader(
    fetchNetworkDataForPrintReport({ stackIds: scopeStackIdsArray }),
  );

  const securityAggregatesProps = useMemo(
    () => ({ orgId, scopeIds: scopedEnvironmentIds }),
    [orgId, scopedEnvironmentIds],
  );

  const viewModel = useSelector((s) =>
    selectSecurityControlAggregates(s, securityAggregatesProps),
  );
  const categoryParamsKey = categoryParams.join(",");
  const frameworkParamsKey = frameworkParams.join(",");

  // Print report is always sorted alphabetically by control name regardless
  // of the dashboard's current sort. This produces a deterministic, easily
  // scannable export that matches the compliance report template.
  const filteredControls = useMemo(() => {
    let controls = viewModel.controls;

    if (categoryParams.length > 0) {
      controls = controls.filter((c) =>
        categoryParams.includes(c.globalControl.area),
      );
    }
    if (frameworkParams.length > 0 && !frameworkParams.includes("all")) {
      controls = controls.filter((c) =>
        c.globalControl.frameworkHandles.some((framework) =>
          frameworkParams.includes(framework),
        ),
      );
    }

    return sortSecurityControlAggregates(controls, "name", "asc", "asc");
  }, [viewModel.controls, categoryParamsKey, frameworkParamsKey]);

  const [referenceImageStatus, setReferenceImageStatus] = useState<
    "pending" | "loaded" | "error"
  >("pending");

  const isLoading =
    !bootupComplete ||
    globalControlsLoader.isLoading ||
    customerControlsLoader.isLoading ||
    networkDataLoader.isLoading;

  const isFullyLoaded =
    !isLoading &&
    (referenceImageStatus === "loaded" || referenceImageStatus === "error");

  useEffect(() => {
    if (isFullyLoaded && onReady) {
      onReady();
    }
  }, [isFullyLoaded, onReady]);

  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange({ isLoading, isFullyLoaded });
    }
  }, [isLoading, isFullyLoaded, onLoadingStateChange]);

  const reportGeneratedAt = useMemo(() => {
    const generated = new Date();
    return {
      at: formatPrintReportCoverGeneratedAt(generated),
      date: formatPrintReportFooterDate(generated),
    };
  }, []);
  const environmentNames =
    scopeEnvironments.map((e) => e.handle).join(", ") || "All Environments";

  const reportFilterLines = useMemo(
    () =>
      buildPrintReportFilterLines({
        categoryParams,
        frameworkParams,
        frameworkMetrics: viewModel.frameworks,
      }),
    [categoryParams, frameworkParams, viewModel.frameworks],
  );

  const activeFrameworkMetricHandles = useMemo(() => {
    if (frameworkParams.length > 0 && !frameworkParams.includes("all")) {
      return frameworkParams;
    }
    return undefined;
  }, [frameworkParams]);

  const filteredFrameworkMetrics = useMemo(
    () =>
      computeFrameworkMetricsFromAggregates(filteredControls, {
        metricHandles: activeFrameworkMetricHandles,
      }),
    [filteredControls, activeFrameworkMetricHandles],
  );

  const complianceOverviewRows = useMemo(
    () =>
      buildPrintReportComplianceOverviewRows(
        filteredFrameworkMetrics,
        frameworkParams,
      ),
    [filteredFrameworkMetrics, frameworkParams],
  );

  const reportScopeSummarySuffix =
    reportFilterLines.length > 0 ? ` ${reportFilterLines.join(" ")}` : "";

  const orgStacks = useSelector(selectStacksByOrgAsList);
  const orgDatabases = useSelector(selectDatabasesByOrgAsList);
  const scopedStacks = useMemo(
    () => partitionScopedStacks(orgStacks, scopeStackIds),
    [orgStacks, scopeStackIds],
  );

  const databaseTypes = useMemo(() => {
    const dbsInScope = orgDatabases.filter((db) =>
      scopeEnvIds.has(db.environmentId),
    );
    const types = [
      ...new Set(dbsInScope.map((db) => getDatabaseLabel(db.type))),
    ].filter(Boolean);
    return types.sort().join(", ") || "—";
  }, [orgDatabases, scopeEnvIds]);

  const orgApps = useSelector(selectAppsByOrgAsList);
  const endpointsList = useSelector(selectEndpointsAsList);
  const servicesTable = useSelector(selectServices);
  const logDrainsList = useSelector(selectLogDrainsAsList);
  const metricDrainsList = useSelector(selectMetricDrainsAsList);
  const vpcPeersList = useSelector(selectVpcPeersAsList);
  const vpnTunnelsList = useSelector(selectVpnTunnelsAsList);

  const infraCounts = useMemo(() => {
    const appsInScope = orgApps.filter((a) => scopeEnvIds.has(a.environmentId));
    const dbsInScope = orgDatabases.filter((db) =>
      scopeEnvIds.has(db.environmentId),
    );
    const endpointsInScope = endpointsList.filter((ep) => {
      const svc = findServiceById(servicesTable, { id: ep.serviceId });
      return svc && scopeEnvIds.has(svc.environmentId);
    });
    const logDrainsInScope = logDrainsList.filter(
      (d) =>
        scopeEnvIds.has(d.environmentId) &&
        d.drainType?.toLowerCase() !== "tail",
    );
    const metricDrainsInScope = metricDrainsList.filter((d) =>
      scopeEnvIds.has(d.environmentId),
    );
    const vpcPeersInScope = vpcPeersList.filter((p) =>
      scopeStackIds.has(p.stackId),
    );
    const vpnTunnelsInScope = vpnTunnelsList.filter((t) =>
      scopeStackIds.has(t.stackId),
    );
    return {
      applicationLayer: appsInScope.length,
      databaseLayer: dbsInScope.length,
      loadBalancers: endpointsInScope.length,
      vpcPeers: vpcPeersInScope.length,
      vpnTunnels: vpnTunnelsInScope.length,
      logDrains: logDrainsInScope.length,
      metricDrains: metricDrainsInScope.length,
    };
  }, [
    orgApps,
    orgDatabases,
    scopeEnvIds,
    scopeStackIds,
    endpointsList,
    servicesTable,
    logDrainsList,
    metricDrainsList,
    vpcPeersList,
    vpnTunnelsList,
  ]);

  const observabilityLogDrainTypes = useMemo(() => {
    const inScope = logDrainsList.filter((d) =>
      scopeEnvIds.has(d.environmentId),
    );
    const types = [...new Set(inScope.map((d) => d.drainType))]
      .filter(Boolean)
      .filter((t) => t.toLowerCase() !== "tail");
    return types.sort().map(titleCase).join(", ") || "—";
  }, [logDrainsList, scopeEnvIds]);

  const observabilityMetricDrainTypes = useMemo(() => {
    const inScope = metricDrainsList.filter((d) =>
      scopeEnvIds.has(d.environmentId),
    );
    const types = [...new Set(inScope.map((d) => d.drainType))].filter(Boolean);
    return types.sort().map(titleCase).join(", ") || "—";
  }, [metricDrainsList, scopeEnvIds]);

  const isolatedVpcsRow = useMemo(() => {
    const { dedicated, shared, selfHosted } = scopedStacks;
    const dedicatedCount = dedicated.length;
    const sharedCount = shared.length;
    const selfHostedCount = selfHosted.length;
    const regionStacks = getIsolatedVpcsRegionStacks(
      dedicated,
      shared,
      selfHosted,
    );
    return {
      countDisplay: buildStackCountDisplay({
        dedicated: dedicatedCount,
        shared: sharedCount,
        selfHosted: selfHostedCount,
      }),
      descriptionText: getIsolatedVpcsDescriptionText(
        dedicatedCount,
        sharedCount,
        selfHostedCount,
      ),
      regions: regionsForStacks(regionStacks),
    };
  }, [scopedStacks]);

  const infrastructureRows = useMemo(
    (): {
      label: string;
      countDisplay: string;
      description: ReactNode;
    }[] => [
      {
        label: "Isolated VPCs",
        countDisplay: isolatedVpcsRow.countDisplay,
        description: (
          <>
            {isolatedVpcsRow.descriptionText} Hosted in{" "}
            <strong>{isolatedVpcsRow.regions}</strong>.
          </>
        ),
      },
      {
        label: "Application Layer",
        countDisplay: `${infraCounts.applicationLayer} Apps`,
        description: (
          <>
            Applications deployed as <strong>Docker containers</strong> and
            managed by the Aptible platform.
          </>
        ),
      },
      {
        label: "Database Layer",
        countDisplay: `${infraCounts.databaseLayer} Managed Databases`,
        description: (
          <>
            Managed databases including <strong>{databaseTypes}</strong>, with
            encryption and automated backups enabled by default.
          </>
        ),
      },
      {
        label: "Load Balancers",
        countDisplay: `${infraCounts.loadBalancers} Endpoints`,
        description: (
          <>
            Access to each stack is controlled by{" "}
            <strong>encrypted endpoints</strong>. TLS endpoints provide load
            balancing and support IP filtering for inbound traffic.
          </>
        ),
      },
      {
        label: "Network Connections",
        countDisplay: networkDataLoader.isSuccess
          ? `${infraCounts.vpnTunnels} VPNs, ${infraCounts.vpcPeers} VPC Peering Connections`
          : "—",
        description: (
          <>
            <strong>Aptible-managed VPN and VPC peering</strong> connections
            provide secure, isolated networking between your stack and other
            networks.
          </>
        ),
      },
      {
        label: "Observability Drains",
        countDisplay: `${infraCounts.logDrains} Log Drains, ${infraCounts.metricDrains} Metric Drains`,
        description: (
          <>
            Connected <strong>Log Drains</strong> (
            <strong>{observabilityLogDrainTypes}</strong>) and{" "}
            <strong>Metric Drains</strong> (
            <strong>{observabilityMetricDrainTypes}</strong>) stream container,
            database, and API logs and metrics to your preferred observability
            or SIEM tools for auditability and monitoring.
          </>
        ),
      },
    ],
    [
      databaseTypes,
      infraCounts,
      isolatedVpcsRow,
      networkDataLoader.isSuccess,
      observabilityLogDrainTypes,
      observabilityMetricDrainTypes,
    ],
  );

  return (
    <div className="print-report-root min-h-screen bg-white">
      <PrintStyles />
      <div className="print-footer" aria-hidden="true">
        <span>{reportGeneratedAt.date}</span>
        <a
          href="https://aptible.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-plum"
        >
          Powered by Aptible
        </a>
      </div>
      <div className="print-container pt-4 pb-8 pl-10 pr-8 min-h-screen">
        {isLoading && (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        )}

        {!isLoading && (
          <>
            <div className="cover-page flex flex-col justify-center pt-6">
              <div className="mb-6">
                <AptibleLogo width={200} />
              </div>
              <h1 className="text-4xl font-bold mb-4">
                Infrastructure Security & Compliance Report
              </h1>
              <p
                className="text-2xl font-semibold mb-4"
                style={{ color: REPORT_BRAND_PURPLE }}
              >
                {organization.name}
              </p>
              <div
                className="cover-generated-box w-fit border border-gray-200 rounded-md px-3 py-2"
                aria-label={`Report generated ${reportGeneratedAt.at}`}
              >
                <p className="text-xs leading-tight text-gray-500">
                  Report generated
                </p>
                <p className="text-sm leading-snug text-gray-900">
                  {reportGeneratedAt.at}
                </p>
              </div>
            </div>

            <div className="intro-page">
              <h1 className="text-3xl font-bold mb-2">About Aptible</h1>
              <p className={`${tokens.type["small normal darker"]} mb-4`}>
                Aptible is a Platform as a Service (PaaS) that provides secure
                infrastructure with built-in automation to support compliance
                frameworks such as HIPAA, SOC 2, HITRUST, and PCI. The platform
                is commonly used by organizations in regulated
                industries—including healthcare, fintech, and enterprise SaaS—to
                deploy and operate applications that handle sensitive data.
              </p>
              <h2 className={`${tokens.type.h3} mb-1 mt-4`}>
                Shared Responsibility Model
              </h2>
              <p className={tokens.type["small normal darker"]}>
                Security within the Aptible platform follows a shared
                responsibility model. Aptible secures the underlying
                infrastructure, including network isolation, encryption systems,
                deployment infrastructure, and platform monitoring.
                Organizations using Aptible are responsible for securing their
                application code, managing access policies, and implementing
                organizational security procedures.
              </p>
              <p className={`${tokens.type["small normal darker"]} mt-2`}>
                <a
                  href="https://www.aptible.com/assets/deploy-division-of-responsibilities.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Division of Responsibilities (PDF)
                </a>
              </p>
              <h2 className={`${tokens.type.h3} mb-1 mt-4`}>
                Reference Architecture
              </h2>
              <div className="rounded-xl border border-gray-300 overflow-hidden mb-2 w-full">
                <img
                  src="/assets/images/report/reference-architecture-diagram.png"
                  alt="Aptible Deploy reference architecture diagram"
                  className="w-full h-auto block"
                  onLoad={() => setReferenceImageStatus("loaded")}
                  onError={() => setReferenceImageStatus("error")}
                />
              </div>
              <p className={tokens.type["small normal darker"]}>
                Full reference:{" "}
                <a
                  href="https://www.aptible.com/assets/deploy-reference-architecture.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  Aptible Reference Architecture
                </a>
              </p>
            </div>

            <div className="infra-compliance-page">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {organization.name} Infrastructure
              </h2>
              <h2 className={`${tokens.type.h3} mb-1 mt-2`}>Summary</h2>
              <p className={`${tokens.type["small normal darker"]} mb-4`}>
                The following summary reflects the infrastructure deployed by{" "}
                {organization.name} within the Aptible platform at the time this
                report was generated. Scoped environments: {environmentNames}.
                {reportScopeSummarySuffix}
                {hasPrintReportControlFilters(
                  categoryParams,
                  frameworkParams,
                ) ? (
                  <>
                    {" "}
                    Compliance status and controls below reflect these filters.
                  </>
                ) : null}
              </p>
              <table className="w-full border-collapse text-sm mb-6">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="py-2 pr-4 text-left font-semibold w-48">
                      Title
                    </th>
                    <th className="py-2 pr-4 text-left font-semibold w-40">
                      Count
                    </th>
                    <th className="py-2 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {infrastructureRows.map((row) => (
                    <tr key={row.label} className="border-b border-gray-200">
                      <td className="py-2 pr-4 font-medium align-top">
                        {row.label}
                      </td>
                      <td className="py-2 pr-4 align-top whitespace-nowrap">
                        {row.countDisplay}
                      </td>
                      <td
                        className={`py-2 ${tokens.type["small normal darker"]}`}
                      >
                        {row.description || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="compliance-overview-section mb-2">
                <h2 className={`${tokens.type.h3} mt-4 mb-2`}>
                  Compliance Status
                </h2>
                <div className="compliance-cards-grid grid grid-cols-3 gap-4 items-stretch">
                  {complianceOverviewRows.map((row) => {
                    const pct = row.percentage;
                    const pctColor =
                      pct === 0
                        ? "text-red-600"
                        : pct === 100
                          ? "text-forest"
                          : "text-amber-700";
                    return (
                      <Box
                        key={row.framework}
                        size="md"
                        className="flex flex-col justify-start border-gray-200 min-w-0 py-2"
                      >
                        <span
                          className={`${tokens.type.h3} ${pctColor} block`}
                          style={
                            pct === 0
                              ? { color: REPORT_STATUS_RED }
                              : pct === 100
                                ? { color: REPORT_STATUS_GREEN }
                                : undefined
                          }
                        >
                          {pct}%
                        </span>
                        <h3
                          className={`${tokens.type["small semibold darker"]} leading-tight break-words min-w-0 mt-1`}
                        >
                          {row.framework}
                        </h3>
                      </Box>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="controls-section">
              <h2 className={`${tokens.type.h3} mb-2`}>Controls Overview</h2>
              <p className={`${tokens.type["small normal darker"]} mb-2`}>
                This report summarizes the Security and Availability controls
                implemented by Aptible on behalf of {organization.name}. Scoped
                environments: {environmentNames}.{reportScopeSummarySuffix}
              </p>
              <div className="border-b border-gray-300 mb-4" />
              {filteredControls.length > 0 ? (
                filteredControls.map((control) => (
                  <ControlPrintItem key={control.id} control={control} />
                ))
              ) : (
                <div
                  className={`text-center py-12 ${tokens.type["small lighter"]}`}
                >
                  No controls found matching the current filters.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const parsePrintControlsParam = (value: string | null): string[] => {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

export const PrintControlsPage = () => {
  const [searchParams] = useSearchParams();
  const autoPrint =
    searchParams.get("autoPrint") === "1" ||
    searchParams.get("autoPrint") === "true";
  const scopeIds = useMemo(
    () => parsePrintControlsParam(searchParams.get("scope")),
    [searchParams],
  );
  const categoryParams = useMemo(
    () => parsePrintControlsParam(searchParams.get("category")),
    [searchParams],
  );
  const frameworkParams = useMemo(
    () => parsePrintControlsParam(searchParams.get("framework")),
    [searchParams],
  );
  const [reportState, setReportState] = useState({
    isLoading: true,
    isFullyLoaded: false,
  });

  useEffect(() => {
    const pylon = (window as Window & { Pylon?: (cmd: string) => void }).Pylon;
    if (typeof pylon === "function") {
      try {
        pylon("hide");
      } catch {
        // no-op
      }
    }
  }, []);

  const handleLoadingStateChange = useCallback(
    (nextState: { isLoading: boolean; isFullyLoaded: boolean }) => {
      setReportState((prevState) => {
        if (
          prevState.isLoading === nextState.isLoading &&
          prevState.isFullyLoaded === nextState.isFullyLoaded
        ) {
          return prevState;
        }
        return nextState;
      });
    },
    [],
  );

  const handlePrint = useCallback(() => {
    if (!reportState.isFullyLoaded) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });
  }, [reportState.isFullyLoaded]);

  return (
    <div
      className={`print-controls-route min-h-screen bg-white${autoPrint ? " auto-print-mode" : ""}`}
    >
      {autoPrint ? (
        <div className="auto-print-message flex flex-col items-center justify-center min-h-[40vh] px-4 text-gray-600">
          {reportState.isFullyLoaded ? (
            <>
              <p className={`${tokens.type.h3} mb-4`}>Report ready</p>
              <Button variant="primary" size="md" onClick={handlePrint}>
                Download PDF
              </Button>
            </>
          ) : (
            <>
              <p className={`${tokens.type.h3} mb-4`}>Preparing your report…</p>
              <LoadingSpinner />
            </>
          )}
        </div>
      ) : null}
      {!reportState.isLoading && !autoPrint ? (
        <div className="no-print sticky top-0 z-10 flex justify-end border-b border-gray-200 bg-white px-8 py-3">
          <Button
            variant="primary"
            size="md"
            onClick={handlePrint}
            disabled={!reportState.isFullyLoaded}
          >
            Print / Save as PDF
          </Button>
        </div>
      ) : null}
      <PrintControlsReportSurface
        scopeIds={scopeIds}
        categoryParams={categoryParams}
        frameworkParams={frameworkParams}
        onLoadingStateChange={handleLoadingStateChange}
      />
    </div>
  );
};
